<template>
  <div class="global-policy-dialog">
    <div class="sticky-header d-flex flex-column py-5">
      <div class="d-flex justify-space-between align-center">
        <div class="headline6" data-testid="global-policy-modal-title">
          {{ $t(`modals.${action}.title`) }}
        </div>
        <v-icon icon="$x" @click="discardSaving()" size="24" />
      </div>
    </div>
    <div class="content-wrapper">
      <div class="d-flex mt-0 h-100">
        <div class="global-policy-dialog-tabs bg-white pr-0">
          <div class="pl-3 pr-3 pt-3">
            <div
              v-if="formValid === false"
              data-testid="global-policy-modal-validation-error-banner"
              class="mb-3 error-block height-unset d-flex align-center"
            >
              <v-icon class="mr-3 ml-4" icon="$warning"></v-icon>
              <span class="body2 text-red-dark">
                {{ $t(`modals.addGlobalMspPolicy.validations.policyNameRequired`) }}
              </span>
            </div>

            <template v-for="(item, index) in messageStack" :key="index">
              <div
                :class="['mb-3 d-flex align-center height-unset', item.blockClass]"
                data-testid="global-policy-modal-requests-error-banner"
              >
                <v-icon class="mr-3 ml-4" :icon="item.icon"></v-icon>
                <span class="body2" :class="item.textClass">
                  {{ item.message }}
                </span>
              </div>
            </template>
          </div>

          <v-list class="mt-4" mandatory v-model:selected="selectedPage">
            <v-list-item
              v-for="(policyTab, i) in policyTabs"
              :key="i"
              :value="policyTab.key"
              :ripple="false"
              color="primary"
              max-height="44px"
              min-height="44px"
              active-class="item-selected"
              class="global-policy-dialog__list"
              :data-testid="`global-policy-modal-policy-item-${policyTab.key}`"
            >
              <template #default>
                <div class="d-flex align-center">
                  <v-icon :icon="policyTab.icon"></v-icon>
                  <span
                    data-testid="global-policy-modal-policy-item-text"
                    class="body2 ml-2"
                    :class="{
                      'text-red-dark': showGeneralInfoError(policyTab.key),
                    }"
                  >
                    {{ $t(`modals.addGlobalMspPolicy.policies.${policyTab.key}.title`) }}
                  </span>
                </div>
              </template>
              <template #append>
                <div class="d-flex align-center">
                  <div
                    v-if="unsavedSections.includes(policyTab.key)"
                    class="unsaved-changes-circle mr-1"
                  ></div>
                  <v-menu
                    offset-y
                    location="bottom left"
                    v-if="
                      policyTab.key !== GlobalPolicySection.GENERAL_INFO && !isViewDefaultPolicy
                    "
                  >
                    <template #activator="{ props }">
                      <v-btn
                        icon="$dots"
                        :ripple="false"
                        variant="plain"
                        v-bind="props"
                        :width="0"
                        :height="20"
                        class="cursor-pointer global-policy-dialog__list__menu"
                        :elevation="0"
                      />
                    </template>
                    <v-list>
                      <v-list-item
                        v-for="action in actionsList"
                        :key="action"
                        :data-testid="`labels-table-action-${action}`"
                        @click="deleteSection(policyTab.key)"
                      >
                        <v-list-item-title>
                          <span>
                            {{ $t(`modals.addGlobalMspPolicy.actions.${action}`) }}
                          </span>
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <template v-if="policyTab.key === GlobalPolicySection.GENERAL_INFO">
                    <div v-if="formValid === false" class="error-circle mr-1"></div>
                  </template>
                </div>
              </template>
            </v-list-item>
          </v-list>
          <div class="w-100">
            <global-policy-selector
              v-if="!isViewDefaultPolicy"
              :selected-items="selectedPolicies"
              @policies-updated="handlePoliciesUpdated($event)"
            />
          </div>
        </div>
        <div class="w-100 bg-indigo-faint pr-6 pl-4 py-6 overflow-auto">
          <v-form class="d-none" ref="form" v-model="formValid">
            <v-text-field
              v-model="localValue.name"
              :rules="[required()]"
              :aria-label="$t('modals.addGlobalMspPolicy.policies.generalInfo.policyName')"
            >
            </v-text-field>
          </v-form>

          <component
            :is="detailsComponent"
            :data="localValue"
            :disabled="isViewDefaultPolicy"
            @update:data="localValue = $event"
            ref="childComponent"
          />
        </div>
      </div>
    </div>
    <div class="sticky-footer">
      <div v-if="!isViewDefaultPolicy">
        <v-btn
          data-testid="global-policy-modal-cancel-button"
          color="primary"
          :ripple="false"
          variant="text"
          class="mr-2"
          @click="discardSaving()"
        >
          {{ $t(`modals.${action}.dismissBtn`) }}
        </v-btn>
        <v-btn
          data-testid="global-policy-modal-action-button"
          color="primary"
          rounded
          elevation="0"
          size="large"
          variant="flat"
          @click="saveSettings()"
        >
          {{ $t(`modals.${action}.actionBtn`) }}
        </v-btn>
      </div>
      <div v-else>
        <v-btn
          data-testid="global-policy-modal-action-button"
          color="primary"
          rounded
          elevation="0"
          size="large"
          variant="flat"
          @click="discardSaving()"
        >
          {{ $t(`modals.${action}.dismissBtn`) }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  type PropType,
  type Ref,
  ref,
} from "vue";
import {
  defaultEmptyPolicies,
  GlobalPolicyAction,
  GlobalPolicyModalAction,
  GlobalPolicySection,
} from "@/constants/msp.ts";
import GeneralInfoSection from "@/components/modals/msp/global-policy/GeneralInfoSection.vue";
import CloudSecuritySection from "@/components/modals/msp/global-policy/CloudSecuritySection.vue";
import GlobalPolicySelector from "@/components/modals/msp/global-policy/GlobalPolicySelector.vue";
import DeviceSection from "@/components/modals/msp/global-policy/DeviceSection.vue";
import EndpointDataGovernanceSection from "@/components/modals/msp/global-policy/EndpointDataGovernanceSection.vue";
import UserDataGovernanceSection from "@/components/modals/msp/global-policy/UserDataGovernanceSection.vue";
import EmailSecuritySection from "@/components/modals/msp/global-policy/EmailSecuritySection.vue";
import EndpointSecuritySection from "@/components/modals/msp/global-policy/EndpointSecuritySection.vue";
import EdrSection from "@/components/modals/msp/global-policy/EdrSection.vue";

import {
  type GlobalPolicyConfig,
  useMspGlobalPoliciesStore,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import { required } from "@/_helpers/validators.ts";
import isEqual from "lodash/isEqual";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { useI18n } from "vue-i18n";
import type { GenericCallback } from "@/types.ts";
import difference from "lodash/differenceBy";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { confirmationDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

export interface MessageStackItem {
  message: string;
  icon: string;
  blockClass: string;
  textClass: string;
}

export interface PolicySaveResult {
  success: boolean;
  error?: unknown;
  section: GlobalPolicySection;
}

export default defineComponent({
  props: {
    action: {
      type: String,
      required: true,
    },
    item: {
      type: Object as PropType<GlobalPolicyConfig>,
      default: () => {
        return {
          ...defaultEmptyPolicies[GlobalPolicySection.GENERAL_INFO],
        };
      },
    },
  },
  components: {
    GlobalPolicySelector,
    GeneralInfoSection,
    DeviceSection,
    CloudSecuritySection,
    EndpointDataGovernanceSection,
    UserDataGovernanceSection,
    EmailSecuritySection,
    EndpointSecuritySection,
    EdrSection,
  },
  emits: ["dismissed", "update:policy"],
  setup(props, { emit }) {
    const selectedPage = ref([GlobalPolicySection.GENERAL_INFO]);
    const mspGlobalPolicyStore = useMspGlobalPoliciesStore();
    let errorTimer: ReturnType<typeof setTimeout>;
    const {
      createGlobalPolicy,
      updateCloudSecurity,
      updateGeneralInfo,
      updateDeviceSettings,
      updateEndpointDataGovernanceSettings,
      updateUserDataGovernanceSettings,
      updateEmailSecuritySettings,
      updateEndpointSecuritySettings,
      updateEdrSettings,
      deleteUserDataGovernanceSection,
      deleteEndpointDataGovernanceSection,
      deleteDevicesSection,
      deleteCloudSecuritySection,
      deleteEmailSecuritySection,
      deleteEndpointSecuritySection,
      deleteEdrSection,
    } = mspGlobalPolicyStore;
    const i18n = useI18n();
    const dialogsStore = useDialogsStore();
    const childComponent = ref();
    const form = ref();
    const snackbarStore = useSnackbarStore();
    const formValid = ref(true);
    const localValue: Ref<GlobalPolicyConfig> = ref({ ...props.item });
    const selectedPolicies = ref([GlobalPolicySection.GENERAL_INFO]);
    const messageStack: Ref<MessageStackItem[]> = ref([]);
    const lastSavedData: Ref<GlobalPolicyConfig> = ref({ ...props.item });
    const actionsList = [GlobalPolicyModalAction.DELETE];
    const isViewDefaultPolicy = computed(() => {
      return props.action === GlobalPolicyAction.VIEW_DEFAULT;
    });
    const policyTabs = computed(() => {
      const keyToIconMap: Record<GlobalPolicySection, string> = {
        [GlobalPolicySection.GENERAL_INFO]: "$activityLogsXs",
        [GlobalPolicySection.CLOUD_SECURITY]: "$cloudSecurityXs",
        [GlobalPolicySection.DEVICE_SETTINGS]: "$settingsXs",
        [GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE]: "$endpointDataGovernanceXs",
        [GlobalPolicySection.USER_DATA_GOVERNANCE]: "$userDataGovernanceXs",
        [GlobalPolicySection.EMAIL_SECURITY]: "$mailUnreadXs",
        [GlobalPolicySection.ENDPOINT_SECURITY]: "$endpointXs",
        [GlobalPolicySection.EDR]: "$edrXs",
      };
      return selectedPolicies.value.map((key) => {
        return {
          key,
          icon: keyToIconMap[key] || "",
        };
      });
    });

    const detailsComponent = computed(() => {
      switch (selectedPage.value[0]) {
        case GlobalPolicySection.GENERAL_INFO:
          return GeneralInfoSection;
        case GlobalPolicySection.CLOUD_SECURITY:
          return CloudSecuritySection;
        case GlobalPolicySection.DEVICE_SETTINGS:
          return DeviceSection;
        case GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE:
          return EndpointDataGovernanceSection;
        case GlobalPolicySection.USER_DATA_GOVERNANCE:
          return UserDataGovernanceSection;
        case GlobalPolicySection.EMAIL_SECURITY:
          return EmailSecuritySection;
        case GlobalPolicySection.ENDPOINT_SECURITY:
          return EndpointSecuritySection;
        case GlobalPolicySection.EDR:
          return EdrSection;
        default:
          return "";
      }
    });

    const isLocalValueChangedFromCreation = computed(() => {
      return !isEqual(lastSavedData.value, localValue.value);
    });

    const showGeneralInfoError = (policyTab: GlobalPolicySection) => {
      return policyTab === GlobalPolicySection.GENERAL_INFO && formValid.value === false;
    };

    const deletedSections: Ref<GlobalPolicySection[]> = ref([]);

    const unsavedSections = computed(() => {
      const sectionExtractors = {
        [GlobalPolicySection.GENERAL_INFO]: (data: GlobalPolicyConfig) => extractGeneralInfo(data),
        [GlobalPolicySection.CLOUD_SECURITY]: (data: GlobalPolicyConfig) =>
          data[GlobalPolicySection.CLOUD_SECURITY],
        [GlobalPolicySection.DEVICE_SETTINGS]: (data: GlobalPolicyConfig) =>
          data[GlobalPolicySection.DEVICE_SETTINGS],
        [GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE]: (data: GlobalPolicyConfig) =>
          data[GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE],
        [GlobalPolicySection.USER_DATA_GOVERNANCE]: (data: GlobalPolicyConfig) =>
          data[GlobalPolicySection.USER_DATA_GOVERNANCE],
        [GlobalPolicySection.EMAIL_SECURITY]: (data: GlobalPolicyConfig) =>
          data[GlobalPolicySection.EMAIL_SECURITY],
        [GlobalPolicySection.ENDPOINT_SECURITY]: (data: GlobalPolicyConfig) =>
          data[GlobalPolicySection.ENDPOINT_SECURITY],
        [GlobalPolicySection.EDR]: (data: GlobalPolicyConfig) => data[GlobalPolicySection.EDR],
      };

      return Object.entries(sectionExtractors)
        .filter(([, extractor]) => {
          const savedData = extractor(lastSavedData.value);
          const currentData = extractor(localValue.value);
          return !isEqual(savedData, currentData);
        })
        .map(([section]) => section as GlobalPolicySection);
    });

    const extractGeneralInfo = (data: GlobalPolicyConfig) => {
      const { name, description } = data;
      return { name, description };
    };

    const saveSettings = async () => {
      const { valid } = await form.value.validate();
      if (childComponent.value.validate) {
        await childComponent.value.validate();
      }
      if (valid) {
        await handleSaveSettings();
      }
    };

    const saveGeneralInfoSection = async () => {
      const hasId = !!localValue.value.id;
      try {
        if (!hasId) {
          const { data } = await createGlobalPolicy(localValue.value);
          localValue.value.id = data.id;
          lastSavedData.value.id = data.id;
        } else {
          await updateGeneralInfo(localValue.value);
        }
        lastSavedData.value.name = localValue.value.name;
        if (localValue.value.description) {
          lastSavedData.value.description = localValue.value.description;
        }
      } catch (e) {
        console.error(e);
        throw e;
      }
    };

    const handleSectionSave = async (
      section: Exclude<GlobalPolicySection, GlobalPolicySection.GENERAL_INFO>
    ) => {
      const callbackMap = {
        [GlobalPolicySection.DEVICE_SETTINGS]: updateDeviceSettings,
        [GlobalPolicySection.CLOUD_SECURITY]: updateCloudSecurity,
        [GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE]: updateEndpointDataGovernanceSettings,
        [GlobalPolicySection.USER_DATA_GOVERNANCE]: updateUserDataGovernanceSettings,
        [GlobalPolicySection.EMAIL_SECURITY]: updateEmailSecuritySettings,
        [GlobalPolicySection.ENDPOINT_SECURITY]: updateEndpointSecuritySettings,
        [GlobalPolicySection.EDR]: updateEdrSettings,
      } as Record<Exclude<GlobalPolicySection, GlobalPolicySection.GENERAL_INFO>, GenericCallback>;
      return callbackMap[section](localValue.value[section], localValue.value.id!);
    };

    const handleSectionDelete = async (
      section: Exclude<GlobalPolicySection, GlobalPolicySection.GENERAL_INFO>
    ) => {
      const callbackMap = {
        [GlobalPolicySection.USER_DATA_GOVERNANCE]: deleteUserDataGovernanceSection,
        [GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE]: deleteEndpointDataGovernanceSection,
        [GlobalPolicySection.DEVICE_SETTINGS]: deleteDevicesSection,
        [GlobalPolicySection.CLOUD_SECURITY]: deleteCloudSecuritySection,
        [GlobalPolicySection.EMAIL_SECURITY]: deleteEmailSecuritySection,
        [GlobalPolicySection.ENDPOINT_SECURITY]: deleteEndpointSecuritySection,
        [GlobalPolicySection.EDR]: deleteEdrSection,
      } as Record<Exclude<GlobalPolicySection, GlobalPolicySection.GENERAL_INFO>, GenericCallback>;
      return callbackMap[section](localValue.value.id!);
    };

    const handleSaveSettings = async () => {
      try {
        const results: PolicySaveResult[] = [];

        // Always save general info first to make sure we have an ID
        if (unsavedSections.value.includes(GlobalPolicySection.GENERAL_INFO)) {
          try {
            await saveGeneralInfoSection();
            results.push({ success: true, section: GlobalPolicySection.GENERAL_INFO });
          } catch (error) {
            results.push({ success: false, error, section: GlobalPolicySection.GENERAL_INFO });
            handleMessageStack(results);
            return; // Stop execution if general info section fails
          }
        }

        // Then save other sections
        for (const section of unsavedSections.value) {
          if (section !== GlobalPolicySection.GENERAL_INFO) {
            try {
              await handleSectionSave(section);
              results.push({ success: true, section });
              // if save is successful, updating last saved data to remove
              // edit circle
              lastSavedData.value = {
                ...lastSavedData.value,
                [section]: { ...localValue.value[section] },
              };
            } catch (error) {
              console.log(error);
              results.push({ success: false, error, section });
              break;
            }
          }
        }

        // Delete items scheduled for deletion
        for (const section of deletedSections.value) {
          if (section !== GlobalPolicySection.GENERAL_INFO) {
            try {
              await handleSectionDelete(section);
              results.push({ success: true, section });
            } catch (error) {
              results.push({ success: false, error, section });
              break;
            }
          }
        }

        if (results.length > 0) {
          handleMessageStack(results);
        }
      } catch (error) {
        console.error(error);
        handleMessageStack([{ success: false, error, section: GlobalPolicySection.GENERAL_INFO }]);
      }
    };

    const handleMessageStack = (results: PolicySaveResult[]) => {
      if (!messageStack.value.length) {
        const someFailed = results.some((result) => !result.success);
        if (someFailed) {
          const failedSections = results
            .filter((result) => !result.success)
            .map((result) => i18n.t(`modals.addGlobalMspPolicy.policies.${result.section}.title`))
            .join(", ");
          messageStack.value.push({
            message: i18n.t("snackbar.messages.globalMspPolicies.policySaveFailed", {
              n: failedSections,
            }),
            icon: "$warning",
            blockClass: "error-block",
            textClass: "text-red-dark",
          });
          errorTimer = setTimeout(() => {
            messageStack.value = [];
          }, 5000);
        } else {
          const snackbarText =
            props.action === GlobalPolicyAction.EDIT
              ? i18n.t("snackbar.messages.globalMspPolicies.policyUpdated")
              : i18n.t("snackbar.messages.globalMspPolicies.policySaved");
          snackbarStore.addGenericSuccess(snackbarText);
          emit("dismissed");
        }
      }
    };

    const closeDialog = () => {
      emit("dismissed");
    };

    const handlePoliciesUpdated = (items: GlobalPolicySection[]) => {
      // Find the last newly added policy so we can focus user on it
      const lastNewPolicy = items.find((item) => !selectedPolicies.value.includes(item));
      selectedPolicies.value = items;
      deletedSections.value = difference(deletedSections.value, items);
      selectedPage.value = lastNewPolicy ? [lastNewPolicy] : [GlobalPolicySection.GENERAL_INFO];

      items.forEach((section) => {
        if (section !== GlobalPolicySection.GENERAL_INFO && defaultEmptyPolicies[section]) {
          if (!localValue.value[section]) {
            localValue.value = { ...localValue.value, [section]: defaultEmptyPolicies[section] };
          }
        }
      });
    };

    const discardSaving = () => {
      if (!isLocalValueChangedFromCreation.value) {
        return closeDialog();
      }
      const dialogConfig = confirmationDialogsConfigConstructor({
        action: GlobalPolicyAction.DISCARD,
        disable: false,
        item: {},
        callback: async () => {
          closeDialog();
        },
      });
      dialogsStore.openDialog({
        ...dialogConfig,
        header: {
          ...dialogConfig.header,
          titleClass: "text-red-dark",
          titleIcon: "warning",
        },
      });
    };

    const deleteSection = (
      section: Exclude<GlobalPolicySection, GlobalPolicySection.GENERAL_INFO>
    ) => {
      const dialogConfig = confirmationDialogsConfigConstructor({
        action: GlobalPolicyModalAction.DELETE,
        disable: false,
        item: {},
        callback: async () => {
          deletedSections.value.push(section);
          delete lastSavedData.value[section];
          selectedPolicies.value = selectedPolicies.value.filter((policy: GlobalPolicySection) => {
            return policy !== section;
          });
          // if we are deleting section on which we currently focused, move to general info
          if (section === selectedPage.value[0]) {
            selectedPage.value = [GlobalPolicySection.GENERAL_INFO];
          }
        },
      });
      dialogsStore.openDialog({
        ...dialogConfig,
        header: {
          ...dialogConfig.header,
          titleClass: "text-red-dark",
          titleIcon: "warning",
        },
      });
    };

    onMounted(() => {
      selectedPolicies.value = [
        ...selectedPolicies.value,
        ...getExistingPolicySections(localValue.value),
      ];
      // If we're duplicating the policy then mark all sections as unsaved
      if (props.action === GlobalPolicyAction.DUPLICATE) {
        lastSavedData.value = {
          ...defaultEmptyPolicies[GlobalPolicySection.GENERAL_INFO],
        };
      }
    });

    onUnmounted(() => {
      clearTimeout(errorTimer);
    });

    function getExistingPolicySections(item: GlobalPolicyConfig): GlobalPolicySection[] {
      return Object.keys(item)
        .filter((key) => Object.values(GlobalPolicySection).includes(key as GlobalPolicySection))
        .map((key) => key as GlobalPolicySection);
    }

    return {
      GlobalPolicySection,
      policyTabs,
      form,
      childComponent,
      detailsComponent,
      selectedPage,
      saveSettings,
      closeDialog,
      localValue,
      selectedPolicies,
      handlePoliciesUpdated,
      required,
      discardSaving,
      formValid,
      messageStack,
      unsavedSections,
      actionsList,
      deleteSection,
      deletedSections,
      isViewDefaultPolicy,
      showGeneralInfoError,
    };
  },
});
</script>

<style scoped lang="scss">
.global-policy-dialog {
  position: relative;
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  &-tabs {
    min-width: 290px;
    border-right: 1px solid rgb(var(--v-theme-indigo-pale)) !important;
  }
  .global-policy-dialog__list__menu:not(:active):not(:focus) {
    opacity: 0;
    transition: all 0.3s ease-in-out;
  }

  &__list:hover .global-policy-dialog__list__menu {
    opacity: 1 !important;
    transition: all 0.3s ease-in-out;
    margin-left: 10px;
    margin-right: 5px;
  }

  .global-policy-dialog__list__menu:active,
  .global-policy-dialog__list__menu:focus {
    opacity: 1 !important;
    transition: all 0.3s ease-in-out;
    margin-left: 10px;
    margin-right: 5px;
  }
}

:deep(*) {
  .icon-action-menu-vertical-xs:before {
    font-size: 20px !important;
  }
}

.content-wrapper {
  flex: 1;
  overflow-y: auto;
}

.item-selected {
  border-left: 3px solid rgb(var(--v-theme-primary)) !important;
}

.v-list-item--density-default.v-list-item--one-line {
  border-left: 3px solid transparent;
}

.sticky-header,
.sticky-footer {
  position: sticky;
  left: 0;
  right: 0;
  background-color: white;
  z-index: 1;
  flex-shrink: 0;
  padding-right: 24px;
  padding-left: 24px;
}

.sticky-header {
  top: 0;
  min-height: 63px;
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale)) !important;
}

.sticky-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid rgb(var(--v-theme-indigo-pale)) !important;
  bottom: 0;
  height: 76px;
}

.unsaved-changes-circle {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: rgb(var(--v-theme-anchor));
}

.error-circle {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: rgb(var(--v-theme-error));
}

.section-action {
  width: 15px;
}

.height-unset {
  height: unset !important;
}
</style>
